import { useEffect } from "react";

/**
 * Per-page SEO meta updater — no external dependency needed.
 * Sets document.title, meta description, canonical URL, and OG/Twitter equivalents
 * so each route shows correct snippets in Google search and social previews.
 *
 * Usage at the top of any page component:
 *   useMeta({
 *     title: "BOTTLE MENU - Tequila | Whiskey | Champagne and More",
 *     description: "Check out the bottle menu at ROSEBAR DC...",
 *     path: "/menu",
 *   });
 */
const SITE_ORIGIN = "https://rosebarlounge.com";
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_e9e78a3a-5557-4749-806a-2569ce606b06/artifacts/fw3e63hf_47495ROSEBAR_WHITE_LOGO-red-rose-480x111-207w.webp";

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function useMeta({ title, description, path, image, noindex }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "content", description);
    if (path) {
      const url = `${SITE_ORIGIN}${path}`;
      setCanonical(url);
      setMeta('meta[property="og:url"]', "content", url);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (description) {
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    setMeta('meta[property="og:image"]', "content", image || DEFAULT_OG_IMAGE);
    setMeta('meta[name="twitter:image"]', "content", image || DEFAULT_OG_IMAGE);
    setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
  }, [title, description, path, image, noindex]);
}
